'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    StoreWidget,
    deleteWidget,
    getWidgetsByCompany,
    updateWidget,
} from '@/lib/api/commerce/store-widgets'
import { useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import { MdAdd, MdDelete, MdEdit, MdWidgets } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

const typeLabels: Record<string, string> = {
    HTML: 'Visual',
    JAVASCRIPT: 'Código',
}

const typeBadgeClass: Record<string, string> = {
    HTML: 'badge-info',
    JAVASCRIPT: 'badge-warning',
}

export default function WidgetsListPage() {
    const navigate = useNavigate()
    const { user } = useAuth()
    const [loading, setLoading] = useState(true)
    const [widgets, setWidgets] = useState<StoreWidget[]>([])
    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    const [deletingId, setDeletingId] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        loadWidgets()
    }, [])

    const loadWidgets = async () => {
        const companyId = user?.company?.id
        if (!companyId) return

        setLoading(true)
        try {
            const res = await getWidgetsByCompany(companyId)
            if (res.success && res.result) {
                setWidgets(
                    Array.isArray(res.result) ? res.result : [res.result],
                )
            }
        } catch {
            toast.error('Erro ao carregar widgets')
        } finally {
            setLoading(false)
        }
    }

    const handleToggleActive = async (widget: StoreWidget) => {
        try {
            const res = await updateWidget(widget.id, {
                active: !widget.active,
            })
            if (res.success) {
                setWidgets((prev) =>
                    prev.map((w) =>
                        w.id === widget.id
                            ? { ...w, active: !w.active }
                            : w,
                    ),
                )
                toast.success(
                    widget.active ? 'Widget desativado' : 'Widget ativado',
                )
            }
        } catch {
            toast.error('Erro ao atualizar widget')
        }
    }

    const handleDelete = async () => {
        if (!deletingId) return

        setDeleting(true)
        try {
            const res = await deleteWidget(deletingId)
            if (res.success) {
                setWidgets((prev) =>
                    prev.filter((w) => w.id !== deletingId),
                )
                toast.success('Widget removido com sucesso')
            } else {
                toast.error('Erro ao remover widget')
            }
        } catch {
            toast.error('Erro ao remover widget')
        } finally {
            setDeleting(false)
            setDeleteModalOpen(false)
            setDeletingId(null)
        }
    }

    if (loading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <span className="loading loading-spinner loading-lg" />
            </div>
        )
    }

    return (
        <div className="mx-auto max-w-5xl space-y-6 p-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <MdWidgets className="text-secondary text-3xl" />
                    <div>
                        <h1 className="text-2xl font-bold">Widgets</h1>
                        <p className="text-base-content/60 text-sm">
                            Código HTML ou JavaScript que roda isolado. Para mostrar na loja, use a seção
                            Widget no editor do tema (Temas da Loja).
                        </p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button
                        className="btn btn-secondary"
                        onClick={() => navigate('/commerce/stores/widgets/new')}
                    >
                        <MdAdd className="text-lg" />
                        Novo Widget
                    </button>
                </div>
            </div>

            {/* Lista */}
            {widgets.length === 0 ? (
                <div className="card bg-base-100 shadow-sm">
                    <div className="card-body items-center text-center">
                        <MdWidgets className="text-base-content/30 text-6xl" />
                        <h3 className="text-lg font-bold">
                            Nenhum widget criado
                        </h3>
                        <p className="text-base-content/60">
                            Crie widgets visuais ou de código para personalizar
                            sua loja.
                        </p>
                        <button
                            className="btn btn-secondary btn-sm mt-2"
                            onClick={() => navigate('/commerce/stores/widgets/new')}
                        >
                            <MdAdd className="text-lg" />
                            Criar Primeiro Widget
                        </button>
                    </div>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>Tipo</th>
                                <th>Status</th>
                                <th className="text-right">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {widgets.map((widget) => (
                                <tr key={widget.id} className="hover">
                                    <td>
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium">
                                                {widget.name}
                                            </span>
                                        </div>
                                        {widget.type === 'HTML' && (
                                            <div
                                                className="text-base-content/50 max-w-xs truncate text-xs"
                                                dangerouslySetInnerHTML={{
                                                    __html: widget.content
                                                        .replace(/<[^>]*>/g, ' ')
                                                        .replace(/\s+/g, ' ')
                                                        .trim()
                                                        .substring(0, 80),
                                                }}
                                            />
                                        )}
                                    </td>
                                    <td>
                                        <span
                                            className={`badge badge-sm ${typeBadgeClass[widget.type] || ''}`}
                                        >
                                            {typeLabels[widget.type] ||
                                                widget.type}
                                        </span>
                                    </td>
                                    <td>
                                        <input
                                            type="checkbox"
                                            className="toggle toggle-secondary toggle-sm"
                                            checked={widget.active}
                                            onChange={() =>
                                                handleToggleActive(widget)
                                            }
                                        />
                                    </td>
                                    <td>
                                        <div className="flex justify-end gap-1">
                                            <button
                                                className="btn btn-ghost btn-sm"
                                                onClick={() =>
                                                    navigate(
                                                        `/commerce/stores/widgets/edit/${widget.id}`,
                                                    )
                                                }
                                                title="Editar"
                                            >
                                                <MdEdit className="text-lg" />
                                            </button>
                                            <button
                                                className="btn btn-ghost btn-sm text-error"
                                                onClick={() => {
                                                    setDeletingId(widget.id)
                                                    setDeleteModalOpen(true)
                                                }}
                                                title="Remover"
                                            >
                                                <MdDelete className="text-lg" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <ConfirmModal
                isOpen={deleteModalOpen}
                onClose={() => {
                    setDeleteModalOpen(false)
                    setDeletingId(null)
                }}
                onConfirm={handleDelete}
                title="Remover Widget"
                message="Tem certeza que deseja remover este widget? Todos os posicionamentos associados serão desativados."
                loading={deleting}
                confirmButtonText="Remover"
                confirmButtonClass="btn-error"
            />
        </div>
    )
}
